import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  ArrowLeft,
  LineChart as LineChartIcon,
  BarChart3,
  ArrowRight
} from "lucide-react";
import { 
  LineChart, 
  Line,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer
} from "recharts";
import { formatCurrency as formatCurrencyUtil } from '../utils/sharedCalculations';
import { usePlanningYears } from '../hooks/usePlanningYears';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForecastingScenarios = () => {
  const navigate = useNavigate();
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [forecastData, setForecastData] = useState(null);
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const planningData = usePlanningYears(currentPlan?.id);
  const { years } = planningData;

  const formatCurrency = (amount, compact = false) => {
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  // Load forecasting data
  const loadForecastingData = async () => {
    if (!currentPlan?.id || !years || years.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch multi-year financial data for forecasting
      const multiYearPromises = years.map(year =>
        axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`)
          .then(res => {
            const income = res.data.financial_statements?.income_statement || {};
            const balance = res.data.financial_statements?.balance_sheet || {};
            return {
              year,
              revenue: income.revenue || 0,
              netIncome: income.net_income || 0,
              assets: balance.total_assets || 0,
              liabilities: balance.total_liabilities || 0
            };
          })
          .catch(() => ({ year, revenue: 0, netIncome: 0, assets: 0, liabilities: 0 }))
      );
      
      const multiYearData = await Promise.all(multiYearPromises);
      setForecastData(multiYearData);
      
      // Fetch real scenarios for comparison
      try {
        const scenariosResponse = await axios.get(`${BACKEND_URL}/api/scenarios/${currentPlan.id}`);
        const scenarios = scenariosResponse.data || [];
        
        if (scenarios.length > 0) {
          // Fetch detailed financial data for each scenario
          const scenarioDetailsPromises = scenarios.slice(0, 3).map(async (scenario) => {
            try {
              // Get the latest year financial data for this scenario
              const latestYear = Math.max(...years);
              
              // Fetch scenario-specific financial statements if available
              // For now, we'll calculate based on scenario assumptions
              const baseFinancial = multiYearData.find(d => d.year === latestYear);
              const baseRevenue = baseFinancial?.revenue || 0;
              const baseNetIncome = baseFinancial?.netIncome || 0;
              
              // Apply scenario adjustments using parameters from the database
              const params = scenario.parameters || {};
              const revenueGrowthMultiplier = 1 + ((params.revenueGrowth || 0) / 100);
              const costReductionMultiplier = 1 - ((params.costReduction || 0) / 100);
              const marginImpactMultiplier = 1 + ((params.operationalEfficiency || 0) / 100);
              
              // Calculate scenario metrics
              const scenarioRevenue = baseRevenue * revenueGrowthMultiplier * (scenario.price_multiplier || 1) * (scenario.volume_multiplier || 1);
              const baseCosts = baseRevenue - baseNetIncome;
              const scenarioCosts = baseCosts * costReductionMultiplier * (scenario.opex_multiplier || 1);
              const scenarioNetIncome = scenarioRevenue - scenarioCosts;
              
              // Calculate profit margin (as ROI equivalent)
              const profitMargin = scenarioRevenue > 0 
                ? (scenarioNetIncome / scenarioRevenue) * 100
                : 0;
              
              // Calculate break-even month (when cumulative profit becomes positive)
              const monthlyRevenue = scenarioRevenue / 12;
              const monthlyCosts = scenarioCosts / 12;
              const monthlyProfit = monthlyRevenue - monthlyCosts;
              let breakEvenMonth = 0;
              if (monthlyProfit > 0) {
                // Already profitable from month 1
                breakEvenMonth = 1;
              } else if (monthlyProfit < 0) {
                // Will not break even within 12 months
                breakEvenMonth = 12;
              } else {
                // Break even exactly
                breakEvenMonth = 12;
              }
              
              // Calculate cash runway (months of operation with current cash)
              // Assuming we have cash reserves equal to 3 months of costs as baseline
              const estimatedCashReserves = baseCosts * 0.25; // 3 months
              const monthlyBurn = monthlyCosts - monthlyRevenue;
              let cashRunway = 0;
              if (monthlyBurn > 0) {
                // Burning cash
                cashRunway = Math.round(estimatedCashReserves / monthlyBurn);
              } else {
                // Cash positive
                cashRunway = 99; // Infinite/sustainable
              }
              
              // Calculate ROI as improvement over baseline
              const baselineMargin = baseRevenue > 0 ? (baseNetIncome / baseRevenue) * 100 : 0;
              const marginImprovement = profitMargin - baselineMargin;
              
              return {
                name: scenario.name || 'Unnamed Scenario',
                description: scenario.description || '',
                revenue: scenarioRevenue,
                netIncome: scenarioNetIncome,
                breakEvenMonth: breakEvenMonth,
                cashRunway: cashRunway,
                roi: profitMargin, // Using profit margin as ROI
                marginImprovement: marginImprovement,
                assumptions: {
                  revenueGrowth: params.revenueGrowth || 0,
                  costReduction: params.costReduction || 0,
                  marginImpact: params.operationalEfficiency || 0
                }
              };
            } catch (error) {
              console.error(`Error processing scenario ${scenario.scenario_name}:`, error);
              return null;
            }
          });
          
          const scenarioDetails = await Promise.all(scenarioDetailsPromises);
          const validScenarios = scenarioDetails.filter(s => s !== null);
          
          if (validScenarios.length > 0) {
            setScenarioComparison(validScenarios);
          }
        }
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        setScenarioComparison(null);
      }
      
    } catch (error) {
      console.error('Error loading forecasting data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForecastingData();
  }, [currentPlan?.id, years]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/planning-dashboard')}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Planning Dashboard
        </Button>
        <TooltipLabel
          label={<h1 className="text-3xl font-bold text-slate-900">Forecasting & Scenarios</h1>}
          tooltip="Financial forecasts, projections, and scenario analysis"
          asChild
        />
        <p className="text-slate-600 mt-1">
          Financial forecasts and scenario comparisons for {currentPlan?.company_name || currentPlan?.name}
        </p>
      </div>

      {/* Revenue Forecast */}
      {forecastData && forecastData.length > 0 && (
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <LineChartIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Revenue Forecast
                </CardTitle>
              }
              tooltip="Revenue Forecast projects total income over 5 years based on growth assumptions, market trends, and sales pipeline. Blue line shows expected revenue trajectory. Analyze for: (1) Growth rate consistency (steady vs. accelerating), (2) Inflection points (when growth changes), (3) Year-over-year increases (e.g., Y1 $500K → Y2 $1M = 100% growth), (4) Realism check (compare with industry benchmarks). Example: SaaS typically 20-40% YoY growth, Retail 5-15% YoY. Flat or declining forecast signals market saturation or competitive pressure. Use with Scenario Comparison to model different growth scenarios (conservative, base, aggressive). Update quarterly based on actual performance."
              asChild
            />
            <CardDescription>5-year revenue projection</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={forecastData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip formatter={(value) => formatCurrency(value, true)} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    name="Projected Revenue"
                    dot={{ fill: '#3b82f6', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profitability Forecast */}
      {forecastData && forecastData.length > 0 && (
        <Card className="border-t-4 border-t-green-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                  Profitability Forecast
                </CardTitle>
              }
              tooltip="Profitability Forecast shows projected net income (final profit after all expenses) over 5 years. Green bars represent bottom-line profit. Key insights: (1) When does business become profitable? (negative bars → positive bars transition), (2) Profit growth rate (increasing bar heights), (3) Profit margin trend (compare with revenue forecast), (4) Sustainability check (consistent positive vs. sporadic). Example: Y1 -$100K (startup losses), Y2 -$20K (nearing break-even), Y3 +$50K (profitable!), Y4 +$150K (scaling), Y5 +$300K (mature). Negative profits burn cash (requires funding); positive profits enable reinvestment. Target: 10-20% net margin (net income ÷ revenue) for healthy business."
              asChild
            />
            <CardDescription>5-year net income projection</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={forecastData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip formatter={(value) => formatCurrency(value, true)} />
                  <Legend />
                  <Bar dataKey="netIncome" fill="#10b981" name="Projected Net Income" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scenario Quick Comparison */}
      {scenarioComparison && scenarioComparison.length > 0 ? (
        <Card className="border-t-4 border-t-cyan-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-cyan-600" />
                  Scenario Quick Comparison
                </CardTitle>
              }
              tooltip="Scenario Quick Comparison analyzes different strategic scenarios side-by-side using real assumptions from Scenario Planning. Each scenario shows: (1) Revenue Growth % assumption applied, (2) Cost Reduction % assumption applied, (3) Margin Impact % assumption applied. Then compares 5 key metrics: Revenue (Year 5), Net Income, Break-Even Month, Cash Runway (months), ROI (%). Example: Conservative (10% growth) vs. Base (25% growth) vs. Aggressive (50% growth) scenarios. Color-coded results: Green = good performance, Yellow = moderate, Red = concerning. Use to: evaluate risk vs. reward, stress-test plans, communicate range of outcomes to investors. Click 'View Detailed Scenario Analysis' to adjust assumptions and create new scenarios."
              asChild
            />
            <CardDescription>Top metrics across different scenarios</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Enhanced Comparison Table */}
              <div className="space-y-4">
                {/* Scenario Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {scenarioComparison.map((scenario, idx) => (
                    <TooltipLabel
                      key={idx}
                      label={
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-lg border-2 border-cyan-200 hover:border-cyan-400 transition-all">
                          <h4 className="font-bold text-slate-900 mb-2">{scenario.name}</h4>
                          {scenario.description && (
                            <p className="text-xs text-slate-600 mb-3">{scenario.description}</p>
                          )}
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Revenue Growth:</span>
                              <span className="font-semibold text-green-600">
                                {scenario.assumptions?.revenueGrowth > 0 ? '+' : ''}{scenario.assumptions?.revenueGrowth}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Cost Reduction:</span>
                              <span className="font-semibold text-blue-600">
                                {scenario.assumptions?.costReduction > 0 ? '+' : ''}{scenario.assumptions?.costReduction}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Margin Impact:</span>
                              <span className="font-semibold text-purple-600">
                                {scenario.assumptions?.marginImpact > 0 ? '+' : ''}{scenario.assumptions?.marginImpact}%
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                      tooltip={`${scenario.name}: Revenue Growth ${scenario.assumptions?.revenueGrowth}%, Cost Reduction ${scenario.assumptions?.costReduction}%, Margin Impact ${scenario.assumptions?.marginImpact}%`}
                    />
                  ))}
                </div>
                
                {/* Comparison Table */}
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gradient-to-r from-cyan-50 to-blue-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-slate-700 font-semibold uppercase tracking-wider text-xs">Metric</th>
                        {scenarioComparison.map((scenario, idx) => (
                          <th key={idx} className="text-right p-3 text-slate-700 font-semibold text-xs">
                            {scenario.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-medium text-slate-600">Revenue (Year 5)</td>
                        {scenarioComparison.map((scenario, idx) => (
                          <td key={idx} className="text-right p-3 text-slate-900 font-semibold">
                            {formatCurrency(scenario.revenue, true)}
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-medium text-slate-600">Net Income</td>
                        {scenarioComparison.map((scenario, idx) => (
                          <td key={idx} className="text-right p-3 font-semibold">
                            <span className={scenario.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(scenario.netIncome, true)}
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-medium text-slate-600">Break-Even Month</td>
                        {scenarioComparison.map((scenario, idx) => (
                          <td key={idx} className="text-right p-3 text-slate-900 font-medium">
                            Month {scenario.breakEvenMonth}
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-medium text-slate-600">Cash Runway</td>
                        {scenarioComparison.map((scenario, idx) => (
                          <td key={idx} className="text-right p-3 font-medium">
                            <span className={scenario.cashRunway >= 12 ? 'text-green-600' : scenario.cashRunway >= 6 ? 'text-yellow-600' : 'text-red-600'}>
                              {scenario.cashRunway} months
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-medium text-slate-600">ROI</td>
                        {scenarioComparison.map((scenario, idx) => (
                          <td key={idx} className="text-right p-3 font-semibold">
                            <span className={scenario.roi >= 15 ? 'text-green-600' : scenario.roi >= 5 ? 'text-yellow-600' : 'text-red-600'}>
                              {scenario.roi.toFixed(1)}%
                            </span>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Link to Full Scenario Modeling */}
              <div className="pt-4 border-t border-slate-200">
                <Button 
                  onClick={() => navigate('/scenario-modeling')} 
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  size="lg"
                >
                  View Detailed Scenario Analysis
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-t-4 border-t-cyan-500">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-cyan-600" />
              Scenario Analysis
            </CardTitle>
            <CardDescription>Create scenarios to compare different planning approaches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 space-y-4">
              <p className="text-slate-500 text-lg">No scenarios created yet</p>
              <p className="text-slate-400 text-sm">
                Create scenarios to model different business outcomes and compare strategies
              </p>
              <Button 
                onClick={() => navigate('/scenario-modeling')} 
                className="bg-cyan-600 hover:bg-cyan-700"
                size="lg"
              >
                Create Your First Scenario
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ForecastingScenarios;
